import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { get } from 'lodash'

import InfiniteSelect from '../../../../components/InfiniteSelect'
import ApiService from '../../../../services/ApiService'
import { paymentMethodLabel } from '../../../../utils/paymentMethodLabels'
import CollapsibleSectionHeader from '../components/CollapsibleSectionHeader'
import { OrderCoreSchemaType } from '../orderCore.schema'
import { FormGrid, FormGroup } from '../styles/shared'
import { Label } from './Details.style'
import { Select } from './Payment.style'

interface PaymentProps {
  /** descrição livre da forma "Outros": existe só no aço */
  methodDescriptionSlot?: React.ReactNode
  onMethodChange?: (method: { id?: number; name: string }) => void
  /** tabela de parcelas: cada tenant tem seu hook e sua gramática de valor */
  children?: React.ReactNode
}

const Payment: React.FC<PaymentProps> = ({ methodDescriptionSlot, onMethodChange, children }) => {
  const [isOpen, setIsOpen] = useState(true)
  const [paymentMethods, setPaymentMethods] = useState<{ id: number; name: string }[]>([])
  const { setValue, watch, formState: { errors } } = useFormContext<OrderCoreSchemaType>()

  const paymentMethodId = watch('paymentMethodId')
  const paymentConditionId = watch('paymentConditionId')
  const paymentConditionName = watch('paymentConditionName')
  const selectedCondition = paymentConditionId ? { id: paymentConditionId, name: paymentConditionName ?? '' } : null

  useEffect(() => {
    ApiService.steelOrders.getPaymentMethods()
      .then((res) => setPaymentMethods(res.data ?? []))
      .catch((err) => console.error(err))
  }, [])

  const fetchPaymentConditions = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1
    const response = await ApiService.steelOrders.getPaymentConditions({ page, limit, search })
    return response.data?.data ?? []
  }, [])

  const createPaymentConditionOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createPaymentCondition({ name })
    return response.data
  }, [])

  const handleSelectCondition = useCallback((option: any) => {
    setValue('paymentConditionId', option?.id, { shouldDirty: true })
    setValue('paymentConditionName', option?.name ?? '', { shouldDirty: true })
  }, [setValue])

  const handleSelectMethod = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? Number(e.target.value) : undefined
    const name = paymentMethods.find((m) => m.id === id)?.name ?? ''
    setValue('paymentMethodId', id, { shouldDirty: true })
    setValue('paymentMethodName', name, { shouldDirty: true })
    onMethodChange?.({ id, name })
  }, [paymentMethods, setValue, onMethodChange])

  return (
    <>
      <CollapsibleSectionHeader
        title="Pagamento"
        isOpen={isOpen}
        onToggle={() => setIsOpen((v) => !v)}
      />

      {isOpen && (
        <>
          <FormGrid>
            <FormGroup>
              <Label htmlFor="paymentConditionId">
                Condição de Pagamento <span style={{ color: '#e53e3e', marginLeft: '4px' }}>*</span>
              </Label>
              <InfiniteSelect
                mode="crud"
                placeholder="Selecione ou crie (ex: 30/60/90)"
                searchPlaceholder="Buscar condição..."
                minCreateLength={2}
                allowDelete={false}
                showSelectedBelow={false}
                value={selectedCondition}
                getOptionLabel={(opt: any) => opt.name}
                hasError={!!get(errors, 'paymentConditionId')}
                fetchFunction={fetchPaymentConditions}
                onItemCreate={createPaymentConditionOption}
                onSelect={handleSelectCondition}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="paymentMethodId">
                Forma de Pagamento <span style={{ color: '#e53e3e', marginLeft: '4px' }}>*</span>
              </Label>
              <Select
                id="paymentMethodId"
                value={paymentMethodId ?? ''}
                onChange={handleSelectMethod}
                hasError={!!get(errors, 'paymentMethodId')}
              >
                <option value="">Selecione</option>
                {paymentMethods.map((method) => (
                  <option key={method.id} value={method.id}>{paymentMethodLabel(method.name)}</option>
                ))}
              </Select>
            </FormGroup>

            {methodDescriptionSlot}
          </FormGrid>

          {children}
        </>
      )}
    </>
  )
}

export default Payment
